/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  GetJobsSortInput,
  SortDirection,
} from '../../../../../__generated__/pgeplus-types';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import theme from '../../../../../themes/theme';

type SortProps = {
  content: PageTextContentType;
  sort: (sortBy: GetJobsSortInput) => void;
  setSortByVal?: Dispatch<SetStateAction<string | undefined>>;
  value?: GetJobsSortInput;
};

export const Sort = (props: SortProps) => {
  const { content, sort, setSortByVal } = props;
  const [val, setVal] = useState<GetJobsSortInput | undefined>(props?.value);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Asc,
  );

  useEffect(() => {
    //Make the sort and direction controlled from the values passed.
    if (props.value) {
      const key = Object.keys(props.value)?.[0];
      if (key) {
        setVal(props.value);
        setSortDirection(Object.values(props.value)?.[0] as SortDirection);
      }
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVal({ [e.target.value]: sortDirection });
    e.target.value === 'createdAt'
      ? setSortByVal && setSortByVal(content.get('REUSABLE_DATE'))
      : e.target.value === 'applicationNo'
      ? setSortByVal && setSortByVal(content.get('REFERENCE_NO'))
      : e.target.value === 'customerName'
      ? setSortByVal && setSortByVal(content.get('CUSTOMER_NAME'))
      : undefined;
    sort && sort({ [e.target.value]: sortDirection });
  };
  const handleSort = (direction: SortDirection) => {
    if (!Boolean(val)) {
      setVal({ createdAt: direction });
      sort({ createdAt: direction });
    }
    setSortDirection(direction);
    val && sort && sort({ ...val, [Object.keys(val)[0]]: direction });
  };

  return (
    <Fragment>
      <Box p={1} pl={1.5} pb={0}>
        <FormControl component="fieldset">
          <RadioGroup
            name="sort-by"
            onChange={handleChange}
            value={val ? Object.keys(val)?.[0] : null}
          >
            <FormControlLabel
              value="createdAt"
              control={<Radio size="small" color="primary" />}
              label={
                <Typography
                  css={{
                    fontSize: theme.spacing(2),
                  }}
                >
                  {content.get('REUSABLE_DATE')}
                </Typography>
              }
            />
            <FormControlLabel
              value="applicationNo"
              control={<Radio size="small" color="primary" />}
              label={
                <Typography
                  css={{
                    fontSize: theme.spacing(2),
                  }}
                >
                  {`${content.get('REFERENCE_NO')}.`}
                </Typography>
              }
            />
            <FormControlLabel
              value="customerName"
              control={<Radio size="small" color="primary" />}
              label={
                <Typography
                  css={{
                    fontSize: theme.spacing(2),
                  }}
                >
                  {content.get('CUSTOMER_NAME')}
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider
        css={{
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(2),
        }}
      />
      <Box p={1} pt={0} pb={2}>
        {(val?.hasOwnProperty('createdAt') || val === undefined) && (
          <Fragment>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <ArrowDownwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  ...(sortDirection === SortDirection.Desc && {
                    fontWeight: 'bold',
                  }),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                }}
                onClick={() => handleSort(SortDirection.Desc)}
              >
                {content.get('NEWEST_TO_OLDEST')}
              </Typography>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                  ...(sortDirection === SortDirection.Asc && {
                    fontWeight: 'bold',
                  }),
                }}
                onClick={() => handleSort(SortDirection.Asc)}
              >
                {content.get('OLDEST_TO_NEWEST')}
              </Typography>
            </Box>
          </Fragment>
        )}
        {val?.hasOwnProperty('applicationNo') && (
          <Fragment>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <ArrowDownwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  ...(sortDirection === SortDirection.Asc && {
                    fontWeight: 'bold',
                  }),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                }}
                onClick={() => handleSort(SortDirection.Asc)}
              >
                {content.get('ASC')}
              </Typography>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                  ...(sortDirection === SortDirection.Desc && {
                    fontWeight: 'bold',
                  }),
                }}
                onClick={() => handleSort(SortDirection.Desc)}
              >
                {content.get('DESC')}
              </Typography>
            </Box>
          </Fragment>
        )}
        {val?.hasOwnProperty('customerName') && (
          <Fragment>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <ArrowDownwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  ...(sortDirection === SortDirection.Asc && {
                    fontWeight: 'bold',
                  }),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                }}
                onClick={() => handleSort(SortDirection.Asc)}
              >
                {content.get('A_TO_Z')}
              </Typography>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpwardIcon />
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  cursor: 'pointer',
                  marginLeft: theme.spacing(1),
                  ...(sortDirection === SortDirection.Desc && {
                    fontWeight: 'bold',
                  }),
                }}
                onClick={() => handleSort(SortDirection.Desc)}
              >
                {content.get('Z_TO_A')}
              </Typography>
            </Box>
          </Fragment>
        )}
      </Box>
    </Fragment>
  );
};
